import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { LanguageToggle } from "@/components/LanguageToggle"
import { Upload, LayoutGrid, ArrowLeft, Sparkles } from "lucide-react"

export function DemoSelectorPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const options = [
    {
      id: "upload",
      title: t("demoSelector.upload.title"),
      description: t("demoSelector.upload.description"),
      icon: Upload,
      features: [
        t("demoSelector.upload.features.formats"),
        t("demoSelector.upload.features.multiple"),
        t("demoSelector.upload.features.relevant"),
      ],
      action: t("demoSelector.upload.action"),
      path: "/demo/upload",
      primary: true,
    },
    {
      id: "template",
      title: t("demoSelector.template.title"),
      description: t("demoSelector.template.description"),
      icon: LayoutGrid,
      features: [
        t("demoSelector.template.features.templates"),
        t("demoSelector.template.features.instant"),
        t("demoSelector.template.features.realistic"),
      ],
      action: t("demoSelector.template.action"),
      path: "/demo/templates",
      primary: false,
    },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("demoSelector.upload.action") === "Select Files" ? "Back" : t("upload.back")}
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-lg hidden sm:block">{t("nav.aiSupportDemo")}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <LanguageToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("demoSelector.title")}
            </h1>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              {t("demoSelector.subtitle")}
            </p>
          </div>

          {/* Options Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {options.map((option) => (
              <Card
                key={option.id}
                className={`relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                  option.primary ? "border-primary/50 ring-1 ring-primary/20" : ""
                }`}
                onClick={() => navigate(option.path)}
              >
                {option.primary && (
                  <div className="absolute top-0 right-0 px-3 py-1 bg-primary text-white text-xs font-medium rounded-bl-lg">
                    {t("demoSelector.recommended")}
                  </div>
                )}
                <CardContent className="p-8">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                    option.primary ? "bg-primary/10" : "bg-secondary/10"
                  }`}>
                    <option.icon className={`w-8 h-8 ${
                      option.primary ? "text-primary" : "text-secondary"
                    }`} />
                  </div>

                  <h2 className="text-2xl font-bold text-foreground mb-3">
                    {option.title}
                  </h2>

                  <p className="text-muted mb-6">
                    {option.description}
                  </p>

                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-foreground">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
                          option.primary ? "bg-primary/10" : "bg-secondary/10"
                        }`}>
                          <svg className={`w-3 h-3 ${
                            option.primary ? "text-primary" : "text-secondary"
                          }`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-full"
                    variant={option.primary ? "default" : "secondary"}
                    size="lg"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(option.path)
                    }}
                  >
                    {option.action}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tip */}
          <div className="mt-8 p-4 rounded-lg bg-secondary/10 border border-secondary/20">
            <p className="text-sm text-center text-secondary-dark">
              <span className="font-medium">💡</span> {t("demoSelector.tip")}
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
